
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AuthUser, AuthProfile } from "@/contexts/AuthContext";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Save, User, Loader2 } from "lucide-react";

const profileFormSchema = z.object({
  fullName: z.string().min(2, {
    message: "Nimi peab olema vähemalt 2 tähemärki pikk",
  }),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

interface ProfileInfoProps {
  user: AuthUser | null;
  profile: AuthProfile | null;
}

const ProfileInfo = ({ user, profile }: ProfileInfoProps) => {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      fullName: profile?.name ?? "",
    },
  });

  useEffect(() => {
    if (profile?.name) {
      profileForm.setValue("fullName", profile.name);
    }
  }, [profile, profileForm]);

  const updateProfile = async (values: ProfileFormValues) => {
    if (!user) return;

    setIsUpdating(true);
    try {
      // Better Auth's updateUser writes the user row (full_name column via
      // the Drizzle `name` alias). One call covers what used to be two
      // Supabase calls (auth metadata + public.users row).
      const { error } = await authClient.updateUser({ name: values.fullName });
      if (error) throw new Error(error.message ?? "Profile update failed");

      toast({
        title: "Profiil uuendatud",
        description: "Sinu profiili andmed on edukalt uuendatud.",
      });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Viga profiili uuendamisel",
        description: error?.message || "Midagi läks valesti. Palun proovi uuesti.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5 text-kaija-accent" />
          Profiili andmed
        </CardTitle>
        <CardDescription>
          Uuenda oma profiili andmeid
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...profileForm}>
          <form
            onSubmit={profileForm.handleSubmit(updateProfile)}
            className="space-y-4"
          >
            <FormField
              control={profileForm.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Täisnimi</FormLabel>
                  <FormControl>
                    <Input placeholder="Sinu täisnimi" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="text-sm text-gray-500 mb-2">
              E-posti aadress: {user?.email}
              <p className="text-xs text-gray-400 mt-1">
                E-posti aadressi muutmiseks võta ühendust administraatoriga.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full md:w-auto"
              disabled={isUpdating}
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvestan...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Salvesta muudatused
                </>
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ProfileInfo;
